import type { FC } from 'react'
import { useState, useEffect, Dispatch, useMemo } from 'react'
import Xarrow, { Xwrapper } from "react-xarrows";
import { HotwashInjectNode, IInjectNode, IInjectInfo, ICOA } from '../../interfaces';
import { isEmpty } from 'lodash';

interface NodeLayerProps {
    injectItem: IInjectInfo;
    setInjectItem: Dispatch<any>;
    hotwashInjects: IInjectInfo[];
    zoomLevel: number;
}

interface HotwashTurn {
    turn: number,
    nodes: HotwashInjectNode[]
}

interface ChoiceMap {
    [index: string]: boolean[], 
}

export const HotwashNodeLayer: FC<NodeLayerProps> = function HotwashNodeLayer({ injectItem, setInjectItem, hotwashInjects, zoomLevel }) {

    const [nodesCoasArray, setNodesCoasArray] = useState<HotwashTurn[]>([])
  
    useEffect(() => {
        const calcPositionsFromInject = (): HotwashTurn[] => {
            let turnsArray: HotwashTurn[] = [];

            for (let i = 0; i<8; i++) {
                let turnObj: HotwashTurn = {
                    turn: i + 1,
                    nodes: []
                };
                //turnObj.turn = i+1;
                //turnObj.nodes = [];
                turnsArray.push(turnObj)
            } 

            if (injectItem.nodes.length > 0) {
                injectItem.nodes.forEach((item: IInjectNode) => {
                    let nodeObj: HotwashInjectNode = {
                        id: item.id,
                        descOpen: item.descOpen,
                        descEditOpen: item.descEditOpen,
                        descHeight: item.descHeight,
                        coaOpen: item.coaOpen,
                        coaEditOpen: item.coaEditOpen,
                        rfiOpen: item.rfiOpen,
                        left_x: 0,
                        left_y: 0,
                        right_x: 0,
                        right_y: 0,
                        coas: item.coas.map(coa => {
                            return {
                                id: coa.id,
                                displayText: coa.displayText,
                                nextNodeId: coa.nextNodeId,
                                x: 0,
                                y: 0,
                                isNew: coa.isNew,
                                v: coa.v,
                                votes: coa.votes,
                                votePercentage: coa.votePercentage,
                                groupChoice: coa.groupChoice
                            }
                        }),
                        timeToLive: item.timeToLive,
                        turn: item.turn,
                    };

                    turnsArray[item.turn-1].nodes.push(nodeObj)
                })
            }

            turnsArray.forEach((turn: HotwashTurn) => {
                if (turn.turn === 1) {
                    if (turn.nodes.length > 0) {
                        let nodePosition: Map<number, {x: number, y: number}> = new Map<number, {x: number; y: number}>()
                        nodePosition.set(2, {x: 388, y: 80})
                        nodePosition.set(3, {x: 296, y: 62})

                        let zoomNodePos = nodePosition.get(zoomLevel)
                        if(zoomNodePos !== undefined)
                        {
                            if(turn.nodes[0].coas.length > 0 ||
                                (turn.nodes[0].timeToLive != null && !isEmpty(turn.nodes[0].timeToLive?.nextNodeId)))
                            {
                                turn.nodes[0].right_x = zoomNodePos.x
                                turn.nodes[0].right_y = zoomNodePos.y
                            }

                        }
                    }
                }
                if (turn.turn > 1) {
                    if (turn.nodes.length > 0) {
                        turn.nodes.forEach((node: HotwashInjectNode, l: number) => {
                            let leftNodePosition: Map<number, {x: number, y: number}> = new Map<number, {x: number; y: number}>()
                            const posMultZoom2X = turn.turn > 2 ? (turn.turn-2) * 326 : 0;
                            const posMultZoom3X = turn.turn > 2 ? (turn.turn-2) * 222 : 0;
                            leftNodePosition.set(2, {x: 482 + posMultZoom2X, y: 60 + (105 * l)}) // (258 * l)
                            leftNodePosition.set(3, {x: 360 + posMultZoom3X, y: 60 + (67 * l)}) // (258 * l)
                            let rightNodePosition: Map<number, {x: number, y: number}> = new Map<number, {x: number; y: number}>()
                            rightNodePosition.set(2, {x:192+521+posMultZoom2X, y:64 + (105 * l)})
                            rightNodePosition.set(3, {x:166+353+posMultZoom3X, y:60 + (67 * l)})
                            const leftZoomNodePos = leftNodePosition.get(zoomLevel)
                            const rightZoomNodePos = rightNodePosition.get(zoomLevel)
                            const hasTimeToLive = node.timeToLive !== null && !isEmpty(node.timeToLive.nextNodeId)
                            if(leftZoomNodePos && rightZoomNodePos) {
                                node.left_x = leftZoomNodePos.x
                                node.left_y = leftZoomNodePos.y
                                if(node.coas.length > 0 || hasTimeToLive) {
                                    node.right_x = rightZoomNodePos.x
                                    node.right_y = rightZoomNodePos.y
                                }
                            }
                        });
                    }
                }
            })

            return turnsArray;
        }

        setNodesCoasArray(calcPositionsFromInject())
    }, [injectItem, setNodesCoasArray, zoomLevel])

    const chosenPaths = useMemo(() => {
        const moveHistory = injectItem.moveHistory.filter((m) => m.injectId === injectItem.injectId && m.type === 'result' && m.options.choiceType === 'coa');
        return (nodesCoasArray && moveHistory)
            ? nodesCoasArray.reduce<ChoiceMap>(
                (choiceMap, turn) => {
                    turn.nodes.forEach((node: HotwashInjectNode) => {
                        choiceMap[node.id] = choiceMap[node.id] || {};
                        node.coas.forEach((coa) => {
                            choiceMap[node.id][coa.id] =
                                moveHistory.findIndex((m) => parseInt(m.choice, 10) === coa.id ) > -1
                        })
                    })
                    return choiceMap;
                }
            , {})
        : {}
    }, [nodesCoasArray, injectItem]);

    return (
        <div className="node-layer">
            {
                nodesCoasArray.map((turn, turnIdx) => {
                    return (turn.nodes.map((node, nodeIdx) => {
                            return (<div>
                                {(node.left_x > 0 && node.left_y > 0) && <div className="node-dot4" id={`elem${node.turn+''+node.id+'_left'}`} style={{ 'top': node.left_y, 'left': node.left_x }}></div>}
                                {(node.right_x > 0 && node.right_y > 0) && <div className="node-dot5" id={`elem${node.turn+''+node.id+'_right'}`} style={{ 'top': node.right_y, 'left': node.right_x }}></div>}
                            </div>)
                        })
                    )
                })
            }
            {nodesCoasArray.map((turn: HotwashTurn, m: number) => {
                return (turn.nodes.map((node: HotwashInjectNode, k: number) => {
                    let findNode = (nodeId: string) => {
                        let foundNode: HotwashInjectNode | undefined = undefined
                        for(let turnIdx = 0; turnIdx < nodesCoasArray.length; turnIdx++)
                        {
                            for(let nodeIdx = 0; nodeIdx < nodesCoasArray[turnIdx].nodes.length; nodeIdx++)
                            {
                                if(nodeId === nodesCoasArray[turnIdx].nodes[nodeIdx].id)
                                {
                                    foundNode = nodesCoasArray[turnIdx].nodes[nodeIdx]
                                    break
                                }
                            }
                            if(foundNode !== undefined) {
                                break
                            }
                        }
                        return foundNode
                    }
                    return (
                        <div key={node.id}>
                                
                                {node.timeToLive !== null && !isEmpty(node.timeToLive.nextNodeId) &&
                                    <div>
                                        <Xwrapper>
                                        <Xarrow
                                            start={`elem${node.turn+''+node.id+'_right'}`}
                                            end={`elem${findNode(node.timeToLive.nextNodeId)!.turn+''+node.timeToLive.nextNodeId+'_left'}`}
                                            showHead={false}
                                            zIndex={1}
                                            strokeWidth={2}
                                            divContainerProps={{id: 'ttldot'}}
                                            divContainerStyle={{opacity: 1.0 /*chosenPaths[node.id][node.timeToLive.nextNodeId] ? 1.0 : 0.5*/}}
                                        />
                                    </Xwrapper> </div>
                                }
                            
                            {(node.coas !== undefined) ? node.coas.map((coaVote1: ICOA, l: number) => {
                                if (coaVote1.nextNodeId !== '') {

                                    return ( <div key={l} className="node-svg" data-nodeid={node.id} data-coaid={coaVote1.id} data-nextnodeid={coaVote1.nextNodeId}><Xwrapper>
                                        <Xarrow
                                            start={`elem${node.turn+''+node.id+'_right'}`}
                                            end={`elem${findNode(coaVote1.nextNodeId)!.turn+''+coaVote1.nextNodeId+'_left'}`}
                                            showHead={false}
                                            zIndex={1}
                                            strokeWidth={2}
                                            divContainerProps={{id: 'nodedot'}}
                                            divContainerStyle={{opacity: chosenPaths[node.id][coaVote1.id] ? 1.0 : 0.5}}
                                        />
                                    </Xwrapper> </div>);
                                } else {
                                    return <div key={l}></div>
                                }
                                
                            }) : <></>}
                        </div>
                    )
                }))
            })}
        </div>
	)
}
